import { getTimeAgo } from "../../app/helpers/helper";

export type SupportStatus =
  | "OPEN"
  | "IN_PROGRESS"
  | "RESOLVED"
  | "CLOSED"
  | "UNKNOWN";

export type SupportTicket = {
  id: string;
  transactionId: string;
  title: string;
  description: string;
  status: SupportStatus;
  createdAt: string | number | null;
  updatedAt: string | number | null;
  hasResponse: boolean;
  raw: any;
};

export type SupportChatMessage = {
  id: string;
  message: string;
  sender: "user" | "support" | "system";
  createdAt: string | number | null;
  raw: any;
};

const toSafeString = (value: any) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const toTimestamp = (value: any) => {
  if (!value) return 0;
  const date = new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? time : 0;
};

const isSuccessCode = (statusCode: any) => {
  const code = Number(statusCode);
  if (Number.isNaN(code)) return false;
  return (code >= 200 && code < 300) || code === 2001;
};

const getRootResult = (response: any) => {
  return (
    response?.data?.result ??
    response?.data?.data ??
    response?.data ??
    response?.result ??
    response ??
    null
  );
};

export const isApiSuccess = (response: any) => {
  if (!response) return false;
  if (isSuccessCode(response?.status)) return true;
  if (isSuccessCode(response?.data?.statusCode)) return true;
  if (response?.data?.status === true) return true;
  if (response?.success === true) return true;
  return false;
};

export const getApiErrorMessage = (
  response: any,
  fallback = "Something went wrong. Please try again.",
) => {
  const message =
    toSafeString(response?.data?.message) ||
    toSafeString(response?.message) ||
    toSafeString(response?.data?.error?.message) ||
    toSafeString(response?.data?.errors?.[0]) ||
    toSafeString(response?.errors?.[0]);
  return message || fallback;
};

export const normalizeSupportStatus = (value: any): SupportStatus => {
  const normalized = toSafeString(value).toUpperCase();
  if (!normalized) return "UNKNOWN";
  if (normalized === "OPEN") return "OPEN";
  if (normalized === "IN_PROGRESS" || normalized === "INPROGRESS") {
    return "IN_PROGRESS";
  }
  if (normalized === "RESOLVED") return "RESOLVED";
  if (normalized === "CLOSED" || normalized === "CLOSE") return "CLOSED";
  if (normalized.includes("PROGRESS")) return "IN_PROGRESS";
  if (normalized.includes("RESOLVE")) return "RESOLVED";
  if (normalized.includes("CLOSE")) return "CLOSED";
  if (normalized.includes("OPEN")) return "OPEN";
  return "UNKNOWN";
};

const isSupportSender = (value: any) => {
  const normalized = toSafeString(value).toUpperCase();
  if (!normalized) return false;
  return (
    normalized.includes("MAINTAINER") ||
    normalized.includes("SUPPORT") ||
    normalized.includes("ADMIN") ||
    normalized.includes("TEAM")
  );
};

const isUserSender = (value: any) => {
  const normalized = toSafeString(value).toUpperCase();
  if (!normalized) return false;
  return (
    normalized.includes("USER") ||
    normalized.includes("CUSTOMER") ||
    normalized.includes("VIEWER")
  );
};

export const getStatusLabel = (status: SupportStatus) => {
  if (status === "OPEN" || status === "IN_PROGRESS") return "In Progress";
  if (status === "RESOLVED") return "Resolved";
  if (status === "CLOSED") return "Closed";
  return "Unknown";
};

export const isTicketActive = (status: SupportStatus) => {
  return status === "OPEN" || status === "IN_PROGRESS";
};

const getArrayFromSource = (source: any, keys: string[]) => {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const getObjectFromSource = (source: any, keys: string[]) => {
  if (source && !Array.isArray(source) && typeof source === "object") {
    return source;
  }
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    const value = source[key];
    if (value && !Array.isArray(value) && typeof value === "object") {
      return value;
    }
  }
  return null;
};

export const resolveTicketId = (value: any) => {
  return (
    toSafeString(value?.ticketId) ||
    toSafeString(value?._id) ||
    toSafeString(value?.id) ||
    toSafeString(value?.ticket?.ticketId) ||
    toSafeString(value?.ticket?.id)
  );
};

const resolveTicketTransactionId = (value: any) => {
  return (
    toSafeString(value?.transactionId) ||
    toSafeString(value?.transaction?.transactionId) ||
    toSafeString(value?.transaction?.id) ||
    toSafeString(value?.referenceId) ||
    toSafeString(value?.meta?.transactionId)
  );
};

export const mapTicket = (ticket: any): SupportTicket | null => {
  const id = resolveTicketId(ticket);
  if (!id) return null;

  const senderValue =
    ticket?.lastMessageBy || ticket?.lastSender || ticket?.senderType || "";
  const hasResponse =
    ticket?.hasResponse === true ||
    Number(ticket?.unreadCount) > 0 ||
    Number(ticket?.responseCount) > 0 ||
    isSupportSender(senderValue);

  const status = normalizeSupportStatus(ticket?.status || ticket?.ticketStatus);

  return {
    id,
    transactionId: resolveTicketTransactionId(ticket),
    title:
      toSafeString(ticket?.title) ||
      toSafeString(ticket?.subject) ||
      toSafeString(ticket?.issue) ||
      toSafeString(ticket?.latestMessage) ||
      "Issue",
    description:
      toSafeString(ticket?.description) ||
      toSafeString(ticket?.message) ||
      toSafeString(ticket?.details),
    status,
    createdAt:
      ticket?.createdTimestamp ||
      ticket?.createdAt ||
      ticket?.created_at ||
      ticket?.timestamp ||
      null,
    updatedAt:
      ticket?.updatedTimestamp ||
      ticket?.updatedAt ||
      ticket?.updated_at ||
      null,
    hasResponse,
    raw: ticket,
  };
};

export const extractTickets = (response: any): SupportTicket[] => {
  const root = getRootResult(response);
  const list = getArrayFromSource(root, ["tickets", "list", "items", "rows"]);
  return list
    .map((item) => mapTicket(item))
    .filter((item): item is SupportTicket => Boolean(item));
};

export const extractTicketDetail = (
  response: any,
  fallbackTicketId?: string,
): SupportTicket | null => {
  const root = getRootResult(response);
  const candidate = getObjectFromSource(root, ["ticket", "details", "item"]);
  const mapped = mapTicket(candidate || root);
  if (mapped) return mapped;
  if (!fallbackTicketId) return null;
  return {
    id: fallbackTicketId,
    transactionId: "",
    title: "Issue",
    description: "",
    status: "UNKNOWN",
    createdAt: null,
    updatedAt: null,
    hasResponse: false,
    raw: {},
  };
};

const mapChatMessage = (chat: any, fallbackIndex: number): SupportChatMessage => {
  const senderType = toSafeString(chat?.senderType).toUpperCase();
  const senderValue =
    senderType ||
    chat?.sender ||
    chat?.from ||
    chat?.role ||
    chat?.createdBy ||
    "";
  const actionType = toSafeString(chat?.actionType).toUpperCase();

  let sender: SupportChatMessage["sender"] = "user";
  if (senderType === "MAINTAINER") {
    sender = "support";
  } else if (senderType === "USER") {
    sender = "user";
  } else if (actionType === "RESOLVE") {
    sender = "system";
  } else if (isSupportSender(senderValue)) {
    sender = "support";
  } else if (isUserSender(senderValue)) {
    sender = "user";
  } else if (toSafeString(chat?.system).toLowerCase() === "true") {
    sender = "system";
  }

  const message =
    toSafeString(chat?.message) ||
    toSafeString(chat?.text) ||
    toSafeString(chat?.description) ||
    toSafeString(chat?.content);

  const createdAt =
    chat?.createdTimestamp ||
    chat?.createdAt ||
    chat?.created_at ||
    chat?.timestamp ||
    chat?.date ||
    null;

  const id =
    toSafeString(chat?.messageId) ||
    toSafeString(chat?.chatId) ||
    toSafeString(chat?.id) ||
    toSafeString(chat?._id) ||
    `${toTimestamp(createdAt)}-${fallbackIndex}`;

  return {
    id,
    message,
    sender,
    createdAt,
    raw: chat,
  };
};

export const extractChats = (response: any): SupportChatMessage[] => {
  const root = getRootResult(response);
  const list = getArrayFromSource(root, ["chats", "messages", "list", "items"]);

  return list
    .map((item, index) => mapChatMessage(item, index))
    .filter((item) => Boolean(item.message))
    .sort((a, b) => {
      const first = toTimestamp(a.createdAt);
      const second = toTimestamp(b.createdAt);
      return first - second;
    });
};

export const getRelativeTime = (
  value: string | number | null,
  t: (key: string) => string,
) => {
  if (!value) return "";
  return getTimeAgo(value, t);
};

export const resolveCreatedTicketId = (response: any) => {
  const root = getRootResult(response);
  if (Array.isArray(root) && root.length) {
    return resolveTicketId(root[0]);
  }
  const ticketId = resolveTicketId(root);
  if (ticketId) return ticketId;

  return (
    toSafeString(root?.ticket?.ticketId) ||
    toSafeString(root?.data?.ticketId) ||
    toSafeString(root?.ticketRef)
  );
};

export const resolveTransactionId = (transaction: any) => {
  return (
    toSafeString(transaction?.transactionId) ||
    toSafeString(transaction?.id) ||
    toSafeString(transaction?._id) ||
    toSafeString(transaction?.txId) ||
    toSafeString(transaction?.referenceId)
  );
};

export const resolveTransactionTitle = (transaction: any) => {
  return (
    toSafeString(transaction?.title) ||
    toSafeString(transaction?.name) ||
    toSafeString(transaction?.event) ||
    toSafeString(transaction?.transactionType) ||
    "Transaction"
  );
};

export const resolveTransactionSubtitle = (transaction: any) => {
  return (
    toSafeString(transaction?.description) ||
    toSafeString(transaction?.details) ||
    toSafeString(transaction?.remarks) ||
    toSafeString(transaction?.note)
  );
};

export const resolveTransactionDate = (transaction: any) => {
  return (
    transaction?.createdTimestamp ||
    transaction?.createdAt ||
    transaction?.created_at ||
    transaction?.date ||
    transaction?.transactionDate ||
    null
  );
};

export const resolveTransactionAmount = (transaction: any) => {
  const value = Number(
    transaction?.transactionAmount ??
      transaction?.coins ??
      transaction?.amount ??
      transaction?.value ??
      0,
  );
  return Number.isFinite(value) ? value : 0;
};

export const buildIssueMessage = (issueTitle: string, description: string) => {
  const issue = toSafeString(issueTitle);
  const details = toSafeString(description);
  if (issue && details) {
    return `${issue}: ${details}`;
  }
  if (details) return details;
  return issue;
};
